import { Button, Spin } from "antd";
import { createRef, useCallback, useContext, useEffect, useRef, useState } from "react";
import { AdminContext, Chat as ChatType } from "context/adminContext";
import chatIcon from "assets/svg/ChatIcon.svg";

import styles from "./Chat.module.css";
import InfiniteScroll from "react-infinite-scroll-component";
import { UploadFilesComponent } from "Components/UploadFilesComponent";
import { PaperClipIcon } from "Common/Icons/PaperClip";
import { getUserSessions, postFileChat } from "helpers/backend_helper";
import { ChatsContext } from "context/chatsContext";
import { SessionChatComponent } from "Components/SessionChatComponent";
import { TakeSessionComponent } from "Components/TakeSessionComponent";
import {
  OtherMessage,
  SessionMessage,
  SupportMessage,
  SYSTEM_MESSAGES,
  SystemMessage,
  UserMessage,
} from "./ChatMessagesComponents";
import { INIT_CHATS } from "helpers/helper";
import { CustomTextArea } from "Components/CustomTextArea";
import { ChatHistoryReloadContext } from "Components/ChatFiles/useChatFileDownload";
import { useRequest } from "ahooks";
import { NotificationContext } from "context/notificationContext";
import { STATUSES } from "../AdminPanel/Chats";
// import { CustomTextArea } from "Components/CustomTextArea";

export type Identifiers = {
  userId: string;
  chatId: string;
  cursor: number;
};

export type Handlers = {
  sendSocketMessage: (content: string, chatId: string) => Promise<any>;
  loadMore: (cursor: number) => void;
  // Свежие ссылки на файлы: по умолчанию первая страница истории
  reloadHistory?: () => void;
  goToUser?: () => void;
  goToUserChat?: () => void;
  onStart?: (isRequest: boolean) => Promise<any>;
  onFinish?: () => Promise<any>;
  onSelectedSession?: (sessionId: string) => void;
  onTakeRequest?: (isRequest: boolean) => Promise<void>;
  onTakeRequestWithoutRequest?: () => void;
  switchChat?: (n: string, switchToChatId?: string) => void;
};

export type UIFlags = {
  loading: boolean;
  hasMore: boolean;
  isOnUser?: boolean;
  onActionLoading?: boolean;
  currentStatus?: STATUSES;
};

export type DataProps = {
  chatData: ChatType;
  action?: string;
};

export type ChatProps = {
  ids: Identifiers;
  handlers: Handlers;
  flags: UIFlags;
  data: DataProps;
};

export const Chat = ({ data, flags, handlers, ids }: ChatProps) => {
  const { chatData, action } = data;
  const { hasMore, loading, isOnUser, onActionLoading, currentStatus } = flags;
  const {
    loadMore,
    sendSocketMessage,
    goToUser,
    onFinish,
    onStart,
    onSelectedSession,
    onTakeRequest,
    switchChat,
    onTakeRequestWithoutRequest,
  } = handlers;
  const { chatId, cursor, userId } = ids;

  const isRequest = currentStatus === STATUSES.WAIT;
  const [textMessage, setTextMessage] = useState("");
  const divRef = createRef<HTMLDivElement>();
  const { localCountry, setChats } = useContext(AdminContext);
  const { bunchFiles, resetInputFields, fileList, setFileList } = useContext(ChatsContext);
  const sendUploadedFiles = useCallback(async () => {
    if (bunchFiles.status === "DONE") {
      if (bunchFiles.files.length) {
        const result = await postFileChat(chatId, { files: bunchFiles.files }, false, {
          headers: {
            "country-iso-code": localCountry.iso,
          },
        });
        console.log("Result of sending files: ", result);
        resetInputFields();
      }
    }
  }, [bunchFiles, chatId, localCountry.iso, resetInputFields]);

  const { openNotificationWithIcon } = useContext(NotificationContext);

  const sendTextHandler = () => {
    if (!isRequest && currentStatus !== STATUSES.FINISHED) {
      if (!textMessage?.trim()) {
        sendUploadedFiles();
        return;
      }
      if (textMessage?.trim()) {
        sendSocketMessage(textMessage, chatId).then(sendUploadedFiles);
        setTextMessage("");
      }
    } else {
      if (textMessage || bunchFiles.status === "DONE") {
        setIsTakeAndSendOpen(true);
      }
    }
  };

  const loadMoreData = () => {
    if (loading) return;
    loadMore(cursor);
  };

  useEffect(() => {
    if (chatId) {
      divRef.current?.scrollTo({ left: 0, top: 0 });
    }
  }, [chatId]);

  const takeAndSendRequest = () => {
    // eslint-disable-next-line no-debugger
    debugger;
    const isRequest = userSessions?.sessions.length
      ? userSessions.sessions.find((s) => s.id === userSessions.lastSessionId).status === "OPEN"
      : false;

    if (currentStatus === STATUSES.FINISHED) {
      if (onTakeRequestWithoutRequest) onTakeRequestWithoutRequest();
      if (!isOnUser) setChats(() => INIT_CHATS);
      if (!textMessage) {
        sendUploadedFiles();
        setIsTakeAndSendOpen(false);
        return;
      }
      sendSocketMessage(textMessage, chatId).then(sendUploadedFiles);
      setTextMessage("");
      if (!isOnUser) {
        setTimeout(() => {
          switchChat("1", chatId);
        });
      }
      setIsTakeAndSendOpen(false);
    } else {
      onTakeRequest(isRequest).then(() => {
        if (!isOnUser) setChats(() => INIT_CHATS);
        if (!textMessage) {
          sendUploadedFiles();
          setIsTakeAndSendOpen(false);
          return;
        }
        sendSocketMessage(textMessage, chatId).then(sendUploadedFiles);
        setTextMessage("");
        setIsTakeAndSendOpen(false);
      });
    }
  };
  console.log(flags);
  const [isTakeAndSendOpen, setIsTakeAndSendOpen] = useState(false);

  const uploadRef = useRef<any>();

  const { loading: userSessionLoading, data: userSessions } = useRequest(
    () => getUserSessions(userId, localCountry?.iso.toUpperCase()),
    {
      ready: !!userId && !!localCountry?.iso,
      refreshDeps: [userId, localCountry?.iso],
      onError: (err: any) => {
        openNotificationWithIcon("error", err.response?.data?.message || err.message);
      },
    },
  );

  // Ссылки на файлы живут 15 минут — при истечении берём свежие из истории
  const reloadHistory = handlers.reloadHistory ?? (() => loadMore(0));

  return (
    <ChatHistoryReloadContext.Provider value={reloadHistory}>
      <div className={styles.chatWrapper}>
        <div className={styles.wrapper}>
          {!isOnUser ? (
            <SessionChatComponent
              actionText={action}
              onAction={() => {
                const isRequest = userSessions?.sessions.length
                  ? userSessions.sessions.find((s) => s.id === userSessions.lastSessionId).status === "OPEN"
                  : false;
                if (action === "Take") {
                  onStart(isRequest);
                } else {
                  onFinish();
                }
              }}
              onGoToUser={() => {
                goToUser();
              }}
              onActionLoading={onActionLoading}
              onSelectedSession={onSelectedSession}
              loadingSessions={userSessionLoading}
              userSessions={userSessions}
            />
          ) : null}
          <div className={styles.chat} id="scrollableElem" ref={divRef}>
            <InfiniteScroll
              dataLength={chatData.messages.length}
              hasMore={hasMore}
              className={styles.chat}
              loader={<Spin />}
              inverse={true}
              next={loadMoreData}
              scrollableTarget="scrollableElem"
            >
              {!chatData.messages?.length ? (
                <div className={styles.noMessages}>
                  <img src={chatIcon} alt="chat icon" />
                  <div>Enter your first message below</div>
                </div>
              ) : null}
              {chatData.messages?.map((messageGroup) => {
                const message = messageGroup?.messages;
                const isFiles = message[0].contentType !== "text";

                if (message[0].messageType === "SYSTEM" && SYSTEM_MESSAGES[message[0].messageKey]) {
                  return (
                    <SystemMessage
                      key={messageGroup.grouping_key}
                      messageId={message[0].id}
                      messageKey={message[0].messageKey}
                      createdAt={message[0].createdAt}
                      params={message[0].messageParams}
                    />
                  );
                }

                if (
                  !message[0].admin &&
                  (message[0].authorType === "CLIENT" ||
                    message[0].author ||
                    (message[0].chatType === "RENT" && message[0].authorId))
                ) {
                  return (
                    <UserMessage
                      key={messageGroup.grouping_key}
                      content={message}
                      isFiles={isFiles}
                      messageCreatedAt={message[0].createdAt}
                      messageId={messageGroup.messages[0].id}
                      avatar={message[0]?.avatar}
                    />
                  );
                } else if (message[0].authorType === "SUPPORT" || message[0].admin) {
                  return (
                    <SupportMessage
                      content={message}
                      isFiles={isFiles}
                      messageCreatedAt={message[0].createdAt}
                      key={messageGroup.grouping_key}
                      messageId={messageGroup.messages[0].id}
                    />
                  );
                } else if (
                  message[0].messageKey === "SESSION_CLOSED" ||
                  message[0].messageKey === "SESSION_OPENED" ||
                  message[0].messageKey === "SESSION_TAKEN"
                ) {
                  return (
                    <SessionMessage
                      content={message}
                      key={messageGroup.grouping_key}
                      messageId={messageGroup.messages[0].id}
                      takenInfo={{
                        closedAt: message[0].messageParams?.closedAt,
                        closedByAdminId: message[0].messageParams?.closedByAdminId,
                        takenAt: message[0].messageParams?.takenAt,
                        takenByAdminId: message[0].messageParams?.takenByAdminId,
                      }}
                    />
                  );
                } else {
                  return (
                    <OtherMessage
                      key={messageGroup.grouping_key}
                      messageId={messageGroup.messages[0].id}
                      messageCreatedAt={message[0].createdAt}
                      isFiles={isFiles}
                      content={message}
                    />
                  );
                }
              })}
            </InfiniteScroll>
          </div>

          <div className={styles.inputBox}>
            <UploadFilesComponent
              fileList={fileList}
              setFileList={setFileList}
              chatId={chatId}
              isArbitr={false}
              uploadRef={uploadRef}
            ></UploadFilesComponent>
            <Button
              type="link"
              className={styles.paperClipBtn}
              icon={<PaperClipIcon />}
              onClick={() => {
                console.log(uploadRef.current);
                if (uploadRef.current) uploadRef?.current?.upload.uploader.fileInput.click();
              }}
            />
            <CustomTextArea
              value={textMessage}
              onChange={(e) => setTextMessage(e.target.value)}
              onKeyDown={(event) => {
                if (event.key === "Enter" && !event.shiftKey) {
                  sendTextHandler();
                  event.preventDefault();
                  event.stopPropagation();
                }
              }}
              isMessage
              style={
                fileList.length ? { borderTopLeftRadius: 0, borderTopRightRadius: 0, borderTopWidth: 0 } : undefined
              }
            />
            <button className={styles.sendButton} onClick={sendTextHandler} />
          </div>
          <TakeSessionComponent
            onTakeAndSand={takeAndSendRequest}
            isOpen={isTakeAndSendOpen}
            onCancel={() => {
              setIsTakeAndSendOpen(false);
            }}
          />
        </div>
      </div>
    </ChatHistoryReloadContext.Provider>
  );
};
